import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { RestockNotify } from "./RestockNotify";

const SERIES_LABELS = { Swimbaits: "PHANTOM", Lizards: "COVERT", Worms: "PULSE" };

export default function ProductCard({ product, index = 0, large = false, colorHex = {} }) {
  const { addItem } = useCart();
  const [pickerOpen, setPickerOpen] = useState(false);
  const soldOut = !product.in_stock || product.stock <= 0;
  const colors = product.colors || [];

  // Close the color picker on any outside click (document-level, since framer-motion
  // transforms break fixed-position overlays by creating a containing block).
  useEffect(() => {
    if (!pickerOpen) return;
    const close = () => setPickerOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [pickerOpen]);

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (colors.length <= 1) {
      addItem(product, colors[0] || null, 1);
      return;
    }
    setPickerOpen((o) => !o);
  };

  const pickColor = (e, c) => {
    e.preventDefault();
    e.stopPropagation();
    setPickerOpen(false);
    addItem(product, c, 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 4) * 0.08, ease: [0.19, 1, 0.22, 1] }}
      className="h-full"
      data-testid="product-card"
    >
      <Link
        to={`/product/${product.id}`}
        className={`group relative flex flex-col h-full bg-[#0C1117] border border-zinc-800/80 hover:border-orange-500/50 hover:shadow-[0_10px_30px_rgba(249,115,22,0.12)] hover:-translate-y-1 transition-[border-color,box-shadow,transform] duration-300 ${soldOut ? "opacity-75" : ""}`}
        data-testid={`product-link-${product.id}`}
      >
        <div className={`relative overflow-hidden bg-[#080D12] border-b border-zinc-800/80 flex items-center justify-center p-4 ${large ? "aspect-[16/10]" : "aspect-square"}`}>
          <img
            src={product.images?.[0]}
            alt={product.name}
            className={`max-h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-105 drop-shadow-[0_10px_15px_rgba(0,0,0,0.8)] ${soldOut ? "grayscale" : ""}`}
            loading="lazy"
          />
          {product.featured && (
            <span className="absolute top-4 left-4 font-mono-plex text-[10px] tracking-[0.2em] uppercase text-orange-400 bg-orange-950/80 border border-orange-500/40 px-2 py-0.5" data-testid="flagship-badge">
              Flagship
            </span>
          )}
          <span className={`absolute ${product.featured ? "top-11" : "top-4"} left-4 font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-300 bg-black/40 backdrop-blur px-2 py-1`}>
            {SERIES_LABELS[product.category] || product.category}
          </span>
          {soldOut ? (
            <span className="absolute top-4 right-4 font-mono-plex text-[10px] tracking-wider uppercase text-zinc-400 bg-zinc-800 border border-zinc-700 px-2 py-0.5" data-testid="sold-out-badge">
              Sold Out
            </span>
          ) : product.stock <= 5 ? (
            <span className="absolute top-4 right-4 font-mono-plex text-[10px] tracking-wider uppercase text-red-400 bg-red-950/60 border border-red-800/60 px-2 py-0.5" data-testid="low-stock-badge">
              Only {product.stock} left
            </span>
          ) : null}
        </div>
        <div className="flex flex-col flex-1 p-5">
          <div className="flex-1">
            <h3 className={`font-display uppercase tracking-tight leading-none ${large ? "text-2xl" : "text-lg"}`}>
              {product.name}
            </h3>
            <p className="mt-2 font-mono-plex text-xs text-zinc-400 tracking-wide">
              {[product.length, product.pack_size].filter(Boolean).join("  //  ")}
            </p>
          </div>
          <div className="relative flex items-center justify-between gap-3 mt-4 pt-3 border-t border-zinc-800/60">
            <span className="font-mono-plex text-sm font-bold text-orange-500 shrink-0">${product.price?.toFixed(2)}</span>
            {product.custom ? (
              <span className="font-mono-plex text-[10px] tracking-[0.15em] uppercase text-orange-400 inline-flex items-center gap-1.5">
                Configure <Plus className="w-3 h-3 rotate-45" />
              </span>
            ) : soldOut ? (
              <RestockNotify productId={product.id} productName={product.name} mode="popover" />
            ) : (
              <>
                {pickerOpen && (
                  <div
                    className="absolute bottom-full right-0 mb-2 z-30 w-60 bg-[#0C1117] border border-zinc-700 shadow-2xl p-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    data-testid={`quick-add-popover-${product.id}`}
                  >
                      <p className="font-mono-plex text-[9px] tracking-[0.2em] uppercase text-zinc-500 px-2 pt-1 pb-2">
                        Pick a color
                      </p>
                      <div className="max-h-48 overflow-y-auto">
                        {colors.map((c) => (
                          <button
                            key={c}
                            onClick={(e) => pickColor(e, c)}
                            className="w-full flex items-center gap-2.5 px-2 py-1.5 text-left hover:bg-white/5 active:scale-[0.98] transition-[background-color,transform] duration-150 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            data-testid={`quick-add-color-${product.id}-${c.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <span
                              className="w-3.5 h-3.5 rounded-full border border-white/20 shrink-0"
                              style={{ background: colorHex[c] || "#3f3f46" }}
                            />
                            <span className="font-mono-plex text-[11px] text-zinc-300">{c}</span>
                          </button>
                        ))}
                      </div>
                  </div>
                )}
                <button
                  onClick={quickAdd}
                  title={colors.length > 1 ? "Pick a color and add to cart" : "Add to cart"}
                  className="font-mono-plex text-[10px] tracking-[0.15em] uppercase font-bold bg-orange-500 text-[#04070A] px-3.5 py-2 inline-flex items-center gap-1.5 hover:bg-orange-600 active:scale-95 transition-[background-color,transform] duration-200 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  data-testid={`quick-add-${product.id}`}
                >
                  <Plus className="w-3 h-3" /> Quick Add
                </button>
              </>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
